import { DifferentiatorOutline, TrophyWithQuestionMark, TwoFaces } from '@lyearn/icons';
import { Stack, Typography } from '@lyearn/ui';

import EmptyState from '../../../KnowledgeAssetDetails/components/EmptyState';

interface CompetitiveAnalysisProps {
  competitors: string;
  differentiators: string;
  winReasons: string;
}

const parsePipeSeparatedString = (text: string): string[] => {
  if (!text) return [];
  return text
    .split('|')
    .map((item) => item.trim())
    .filter(Boolean);
};

const CompetitiveAnalysis = ({
  competitors,
  differentiators,
  winReasons,
}: CompetitiveAnalysisProps) => {
  const competitorPoints = parsePipeSeparatedString(competitors);
  const differentiatorPoints = parsePipeSeparatedString(differentiators);
  const winReasonPoints = parsePipeSeparatedString(winReasons);

  return (
    <Stack className="w-full xs:min-w-full xs:p-12 xs:mt-12" direction="col" gapY={32}>
      <Stack className="grid grid-cols-2 gap-16 xs:grid-cols-1">
        {/* Why did we win Section */}
        <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
          <Stack alignItems="center" gapX={8}>
            <TrophyWithQuestionMark fillColorToken="icon-active" height={24} width={24} />
            <Typography variant="heading-xs">Why did we win?</Typography>
          </Stack>
          <Stack direction="col" gapY={12}>
            {winReasonPoints.length > 0 ? (
              winReasonPoints.map((point, index) => (
                <Stack key={index} direction="col" gapY={12}>
                  <Typography className="text-text-secondary" variant="body-long-01">
                    {point}
                  </Typography>
                  {index !== winReasonPoints.length - 1 && (
                    <div className="border-b border-border" />
                  )}
                </Stack>
              ))
            ) : (
              <EmptyState />
            )}
          </Stack>
        </Stack>

        {/* Differentiators Section */}
        <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
          <Stack alignItems="center" gapX={8}>
            <DifferentiatorOutline fillColorToken="icon-active" height={24} width={24} />
            <Typography variant="heading-xs">What were our differentiators?</Typography>
          </Stack>
          <Stack direction="col" gapY={12}>
            {differentiatorPoints.length > 0 ? (
              differentiatorPoints.map((point, index) => (
                <Stack key={index} direction="col" gapY={12}>
                  <Typography className="text-text-secondary" variant="body-long-01">
                    {point}
                  </Typography>
                  {index !== differentiatorPoints.length - 1 && (
                    <div className="border-b border-border" />
                  )}
                </Stack>
              ))
            ) : (
              <EmptyState />
            )}
          </Stack>
        </Stack>

        {/* Competitors Section */}
        <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
          <Stack alignItems="center" gapX={8}>
            <TwoFaces fillColorToken="icon-active" height={24} width={24} />
            <Typography variant="heading-xs">Competitor</Typography>
          </Stack>
          <Stack direction="col" gapY={12}>
            {competitorPoints.length > 0 ? (
              competitorPoints.map((point, index) => (
                <Stack key={index} direction="col" gapY={12}>
                  <Typography className="text-text-secondary" variant="body-long-01">
                    {point}
                  </Typography>
                  {index !== competitorPoints.length - 1 && (
                    <div className="border-b border-border" />
                  )}
                </Stack>
              ))
            ) : (
              <EmptyState />
            )}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default CompetitiveAnalysis;
